import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import {
  createStripeClient,
  getStripeErrorMessage,
  type StripeEnv,
} from "@/lib/stripe.server";
import { notifyAdminOfPaidOrder } from "@/lib/email/notify-admin.server";

const URL_REGEX = /^https?:\/\/[^\s]+\.[^\s]+/i;
const PLAN_IDS = [
  "starter",
  "pro",
  "agency",
  "agency_bundle",
  "agency_monthly",
  "teardown",
] as const;

export interface PublicPlan {
  id: string;
  name: string;
  description: string;
  price_cents: number;
  currency: string;
  eta_days: number;
  delivery_label: string;
  features: string[];
  is_contact_sales: boolean;
  sort_order: number;
}

export const listPlans = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ plans: PublicPlan[] }> => {
    const { data, error } = await supabaseAdmin
      .from("plans")
      .select(
        "id,name,description,price_cents,currency,eta_days,delivery_label,features,is_contact_sales,sort_order",
      )
      .eq("active", true)
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return {
      plans: (data ?? []).map((p) => ({
        ...p,
        features: Array.isArray(p.features) ? (p.features as string[]) : [],
      })),
    };
  },
);

const IntakeSchema = z.object({
  // Self-serve checkout: GEO audit (starter/pro) + Performance Teardown.
  // Agency plans go through a separate inquiry/subscription path.
  plan_id: z.enum(["starter", "pro", "teardown"]),
  website_url: z
    .string()
    .trim()
    .min(4)
    .max(500)
    .regex(URL_REGEX, "Enter a valid URL starting with http(s)://"),
  full_name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(255),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  industry: z.string().trim().max(120).optional().or(z.literal("")),
  competitors: z.string().trim().max(1000).optional().or(z.literal("")),
  target_audience: z.string().trim().max(1000).optional().or(z.literal("")),
  notes: z.string().trim().max(4000).optional().or(z.literal("")),
  environment: z.enum(["sandbox", "live"]),
  return_origin: z.string().url(),
});

type CheckoutResult = { clientSecret: string; orderNumber: string } | { error: string };

/**
 * Creates the order row (status=pending_payment) then opens an embedded
 * Stripe Checkout session. Returns the client secret + order number so the
 * UI can mount the inline form and recover state on the return page.
 */
export const createCheckoutSession = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => IntakeSchema.parse(data))
  .handler(async ({ data }): Promise<CheckoutResult> => {
    // Resolve plan + price first (server-side source of truth for amount).
    const { data: plan, error: planErr } = await supabaseAdmin
      .from("plans")
      .select("id,name,price_cents,currency")
      .eq("id", data.plan_id)
      .eq("active", true)
      .maybeSingle();
    if (planErr) return { error: planErr.message };
    if (!plan) return { error: "Plan not available" };
    if (plan.price_cents <= 0) return { error: "This plan does not support self-serve checkout" };

    // Insert order row first so we have an order_number to thread through.
    const { data: inserted, error: insertErr } = await supabaseAdmin
      .from("orders")
      .insert({
        // trigger overwrites with SG-XXXXXX; placeholder keeps the type happy
        order_number: "",
        plan_id: plan.id,
        status: "pending_payment",
        email: data.email,
        full_name: data.full_name,
        company: data.company || null,
        industry: data.industry || null,
        website_url: data.website_url,
        competitors: data.competitors || null,
        target_audience: data.target_audience || null,
        notes: data.notes || null,
        amount_cents: plan.price_cents,
        currency: plan.currency,
      })
      .select("id,order_number")
      .single();
    if (insertErr) return { error: insertErr.message };

    try {
      const stripe = createStripeClient(data.environment as StripeEnv);
      const priceLookupKey =
        plan.id === "teardown"
          ? "perf_teardown_onetime"
          : plan.id === "starter"
            ? "geo_starter_onetime"
            : "geo_pro_onetime";
      const productKind = plan.id === "teardown" ? "teardown" : "geo_audit";
      const prices = await stripe.prices.list({ lookup_keys: [priceLookupKey] });
      if (!prices.data.length) {
        return { error: "Price not found in payment provider" };
      }
      const stripePrice = prices.data[0];
      const productId =
        typeof stripePrice.product === "string"
          ? stripePrice.product
          : stripePrice.product.id;
      const product = await stripe.products.retrieve(productId);

      const returnUrl = `${data.return_origin}/order-confirmed?order=${encodeURIComponent(
        inserted.order_number,
      )}&session_id={CHECKOUT_SESSION_ID}`;

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: stripePrice.id, quantity: 1 }],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: returnUrl,
        customer_email: data.email,
        payment_intent_data: { description: product.name },
        metadata: {
          order_id: inserted.id,
          order_number: inserted.order_number,
          plan_id: plan.id,
          product_kind: productKind,
        },
      });

      await supabaseAdmin
        .from("orders")
        .update({ stripe_session_id: session.id })
        .eq("id", inserted.id);

      return {
        clientSecret: session.client_secret ?? "",
        orderNumber: inserted.order_number,
      };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

const ConfirmSchema = z.object({
  order_number: z.string().min(3).max(40),
  session_id: z.string().min(3).max(200).optional(),
  environment: z.enum(["sandbox", "live"]).optional(),
});

export interface PublicOrderSummary {
  order_number: string;
  status: string;
  plan_id: string;
  plan_name: string;
  delivery_label: string;
  amount_cents: number;
  currency: string;
  estimated_delivery_at: string | null;
  paid_at: string | null;
  // PII fields, only present when caller proved possession of the Stripe
  // checkout session_id matching this order.
  website_url?: string;
  email?: string;
  full_name?: string;
}

/**
 * Used by /order-confirmed. If a Stripe session_id is present, we verify it
 * with Stripe and flip the order to status=pending + paid_at on success.
 * Idempotent, the webhook may have already done this.
 */
export const confirmOrderPayment = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ConfirmSchema.parse(data))
  .handler(async ({ data }): Promise<{ order: PublicOrderSummary } | { error: string }> => {
    let verifiedSessionId: string | null = null;
    if (data.session_id && data.environment) {
      try {
        const stripe = createStripeClient(data.environment);
        const session = await stripe.checkout.sessions.retrieve(data.session_id);
        const orderNumberFromMeta = session.metadata?.order_number;
        if (orderNumberFromMeta && orderNumberFromMeta !== data.order_number) {
          return { error: "Order mismatch" };
        }
        if (orderNumberFromMeta === data.order_number) {
          verifiedSessionId = data.session_id;
        }
        if (session.payment_status === "paid") {
          const { data: updated } = await supabaseAdmin
            .from("orders")
            .update({
              status: "pending",
              paid_at: new Date().toISOString(),
              stripe_payment_intent:
                typeof session.payment_intent === "string"
                  ? session.payment_intent
                  : session.payment_intent?.id ?? null,
            })
            .eq("order_number", data.order_number)
            .eq("status", "pending_payment")
            .select(
              "id,order_number,plan_id,amount_cents,currency,full_name,email,website_url,company,industry,competitors,target_audience,notes, plans(name,delivery_label)",
            );
          // Only the first transition wins, perfect dedupe with the
          // Stripe webhook handler.
          const row = updated?.[0];
          if (row) {
            const planRel = (row as unknown as { plans?: { name: string; delivery_label: string } | null }).plans;
            await notifyAdminOfPaidOrder({
              order_id: row.id,
              order_number: row.order_number,
              plan_name: planRel?.name ?? row.plan_id,
              amount_cents: row.amount_cents,
              currency: row.currency,
              full_name: row.full_name,
              email: row.email,
              website_url: row.website_url,
              company: row.company,
              industry: row.industry,
              competitors: row.competitors,
              target_audience: row.target_audience,
              notes: row.notes,
              delivery_label: planRel?.delivery_label ?? null,
            });
          }
        }
      } catch (error) {
        // Don't fail the page, just continue with whatever we have in DB
        console.error("Stripe session verify failed:", getStripeErrorMessage(error));
      }
    }

    const { data: row, error } = await supabaseAdmin
      .from("orders")
      .select(
        "order_number,status,plan_id,website_url,amount_cents,currency,email,full_name,estimated_delivery_at,paid_at,stripe_session_id, plans(name,delivery_label)",
      )
      .eq("order_number", data.order_number)
      .maybeSingle();
    if (error) return { error: error.message };
    if (!row) return { error: "Order not found" };
    const planRel = (row as unknown as { plans?: { name: string; delivery_label: string } | null }).plans;
    // Authorize PII exposure only when the caller supplied a session_id that
    // matches this order's recorded Stripe session. Prevents enumeration of
    // SG-XXXXXX order numbers from leaking customer email/name.
    const piiAuthorized =
      !!verifiedSessionId &&
      !!row.stripe_session_id &&
      verifiedSessionId === row.stripe_session_id;
    return {
      order: {
        order_number: row.order_number,
        status: row.status,
        plan_id: row.plan_id,
        plan_name: planRel?.name ?? row.plan_id,
        delivery_label: planRel?.delivery_label ?? "",
        amount_cents: row.amount_cents,
        currency: row.currency,
        estimated_delivery_at: row.estimated_delivery_at,
        paid_at: row.paid_at,
        ...(piiAuthorized
          ? {
              website_url: row.website_url,
              email: row.email,
              full_name: row.full_name,
            }
          : {}),
      },
    };
  });

const AgencySchema = z.object({
  full_name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email().max(255),
  company: z.string().trim().max(200).optional().or(z.literal("")),
  website: z.string().trim().max(500).optional().or(z.literal("")),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
});

export const submitAgencyInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => AgencySchema.parse(data))
  .handler(async ({ data }): Promise<{ ok: true } | { error: string }> => {
    const { error } = await supabaseAdmin.from("agency_inquiries").insert({
      full_name: data.full_name,
      email: data.email,
      company: data.company || null,
      website: data.website || null,
      message: data.message || null,
    });
    if (error) return { error: error.message };
    return { ok: true };
  });